
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SelectionInfo,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import getChannelSubscriptionStatus, {
  ChannelSubscriptionStatus,
  SubscriptionChannel,
} from '@/composables/contacts/channelSubscription';
import { showToastError, showToastSuccess } from '@/helpers';
import {
  defineComponent, onBeforeMount, ref,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import InlineMessage from 'primevue/inlinemessage';
import Tooltip from 'primevue/tooltip';
import Flag from '@/components/table/renderers/Flag.vue';
import ImportModal from '@/components/import/ImportModal.vue';
import ListSegmentationEditor from '@/components/mes-lists/ListSegmentationEditor.vue';
import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import { ListType } from '@/types';
import {
  CopyToListAction,
  MoveToListAction,
  CreateListAction,
  SubscribeAction,
  UnsubscribeAction,
  DeleteFromListAction,
  DeleteFromAllListsAction,
  GdprEraseAction,
  useContactsActions,
  useOptinFilterFields,
} from './ListsContactsActions';
import type {
  CopyToListResult,
  MoveToListResult,
  CreateListResult,
  SubscribeResult,
  UnsubscribeResult,
  DeleteFromListResult,
  DeleteFromAllListsResult,
  GdprEraseResult,
} from './ListsContactsActions';

export default defineComponent({
  name: 'ListsContacts',

  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmButton,
    InlineMessage,
    Flag,
    CopyToListAction,
    MoveToListAction,
    CreateListAction,
    SubscribeAction,
    UnsubscribeAction,
    DeleteFromListAction,
    DeleteFromAllListsAction,
    GdprEraseAction,
    ImportModal,
    ListSegmentationEditor,
    DataExplorerLink,
  },

  directives: {
    Tooltip,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  setup(props: { id: string}) {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const childComponentRef = ref();
    const listName = ref('');
    const listType = ref('');
    const isListSegmented = ref(false);
    const showSegmentationEditor = ref(false);
    const countRequestListImport = ref(0);
    const apiCall = ref<ApiCallConfig | null>(null);
    const showImportModal = ref(false);

    // Action component refs
    const copyToListRef = ref(null);
    const moveToListRef = ref(null);
    const createListRef = ref(null);
    const subscribeRef = ref(null);
    const unsubscribeRef = ref(null);
    const deleteFromListRef = ref(null);
    const deleteFromAllListsRef = ref(null);
    const gdprEraseRef = ref(null);

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    // Use the composable for grouped and row actions
    const { groupedActions, rowActions } = useContactsActions({
      copyToListRef,
      moveToListRef,
      createListRef,
      subscribeRef,
      unsubscribeRef,
      deleteFromListRef,
      deleteFromAllListsRef,
      gdprEraseRef,
      listType,
    });

    apiCall.value = {
      type_api: NestApiType.CLIENT_API,
      path: `/lists/${props.id}/contacts`,
      secure: true,
    };

    const columns: SpmColumnConfig[] = [
      // Hidden: ID for selection/actions
      {
        id: 'id_contact',
        header: '',
        fields: ['id_contact', 'id_customer'],
        hidden: true,
      },
      // Composite: fullname with firstname, lastname, created_at, updated_at
      {
        id: 'fullname',
        header: t('myLists.manage.customers.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('myLists.manage.customers.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('myLists.manage.customers.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myLists.manage.customers.headers.creationDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myLists.manage.customers.headers.modifDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      // Single field columns
      {
        id: 'email',
        header: t('myLists.manage.customers.headers.email'),
        fields: [{
          name: 'email',
          label: t('myLists.manage.customers.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'sms',
        header: t('myLists.manage.customers.headers.sms'),
        fields: [{
          name: 'phone',
          label: t('myLists.manage.customers.headers.sms'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'push',
        header: t('myLists.manage.customers.headers.push'),
        fields: ['push'],
      },
      // Composite: location with country and lang
      {
        id: 'location',
        header: t('myLists.manage.customers.headers.location'),
        fields: [{
          name: 'country',
          label: t('myLists.manage.customers.headers.country'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'countrySelect',
          },
        }, {
          name: 'lang',
          label: t('myLists.manage.customers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      // Hidden: expandable data for subscription status
      {
        id: 'additional_data',
        header: '',
        fields: [...useOptinFilterFields(), 'unsub_email', 'unsub_sms', 'unsub_push'],
        hidden: true,
      },
    ];

    /**
     * Fetch list details (name and type) from API
     */
    const fetchListDetails = async () => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/lists/${props.id}`,
          {},
          '',
          undefined,
          true,
          { fields: ['name', 'type'] },
        );

        if (response && response.statusCode === 200 && response.data.name) {
          listName.value = response.data.name;
          listType.value = response.data.type || ListType.NOT_DEFINED;
          isListSegmented.value = response.data.type === 'segment';
        } else {
          showToastError(t('myLists.manage.customers.errors.listNotFound'));
          router.push('/lists/manage');
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        showToastError(t('myLists.manage.customers.errors.listNotFound'));
        router.push('/lists/manage');
      }
    };

    onBeforeMount(async () => {
      await fetchListDetails();

      // Check if we should open import modal (from list creation)
      if (route.query.openImport === 'true') {
        showImportModal.value = true;
        // Clean up the query param
        router.replace({ query: {} });
      }
    });

    /**
     * Handle manage segmentation button click
     */
    const handleManageSegmentation = () => {
      showSegmentationEditor.value = true;
    };

    /**
     * Handle import reports button click
     * Redirects to the import reports page for this list
     */
    const handleImportReports = () => {
      router.push(`/lists/import-report/contacts/${props.id}`);
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Method to format phone numbers
    const formatPhoneNumber = (phone: string) => {
      if (!phone || phone === 'null') return '';
      // Remove all non-digit characters except +
      const cleaned = phone.replace(/[^\d+]/g, '');
      // Format with spaces every 2 digits for readability
      if (cleaned.startsWith('+')) {
        const countryCode = cleaned.slice(0, 3);
        const rest = cleaned.slice(3);
        return `${countryCode} ${rest.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
      }
      return cleaned.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (childComponentRef.value?.unselectAll) {
        childComponentRef.value.unselectAll();
      }
    };

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (childComponentRef.value?.reloadData) {
        childComponentRef.value.reloadData();
      }
    };

    /**
     * Handle segmentation saved event
     */
    const onSegmentationSaved = () => {
      showSegmentationEditor.value = false;
      // Reload table data to reflect changes
      reloadTableData();
    };

    // ========== List Actions API ==========

    /**
     * Build selection payload for API
     * @param selectionInfo - Selection info from table (grouped mode)
     * @param rowId - Single row ID (row mode)
     * @returns Object with selection_type, selected_ids, and filters
     */
    const buildSelectionPayload = (
      selectionInfo?: SelectionInfo | null,
      rowId?: number | string | null,
    ): { selection_type: string; selected_ids: string[]; filters?: SpmApiFilter[] } => {
      if (selectionInfo) {
        // Grouped mode
        if (selectionInfo.selectAllMode) {
          return {
            selection_type: 'all',
            selected_ids: selectionInfo.excludedRows ?? [],
            filters: selectionInfo.filters,
          };
        }
        return {
          selection_type: 'manual',
          selected_ids: selectionInfo.selectedRows,
        };
      }
      // Single row mode
      return {
        selection_type: 'manual',
        selected_ids: rowId ? [String(rowId)] : [],
      };
    };

    /**
     * Call list actions API
     * @param payload - Action payload to send
     * @returns API response
     */
    const callListActionsApi = async (payload: Record<string, unknown>) => {
      try {
        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/list-actions',
          {}, // headers
          '', // query
          payload, // data (body)
          true, // secure
        );
        return response;
      } catch (error) {
        console.error('List action API error:', error);
        return { success: false, message: 'api_error' };
      }
    };

    /**
     * Handle API response and show appropriate toast
     * @param response - API response
     * @param successKey - Translation key for success message
     * @param isGrouped - Whether this was a grouped action
     */
    const handleActionResponse = (
      response: { success?: boolean; message?: string },
      successKey: string,
      isGrouped: boolean,
    ) => {
      if (response.success) {
        if (response.message === 'request_pending') {
          // Queued for async processing
          showToastSuccess(t('myLists.manage.customers.actions.queuedSuccess'));
        } else {
          // Immediate success
          showToastSuccess(t(successKey));
        }
        if (isGrouped) {
          resetTableSelection();
        }
        reloadTableData();
      } else {
        // Error
        const errorMessage = response.message || 'unknown_error';
        showToastError(t('myLists.manage.customers.actions.error', { message: errorMessage }));
      }
    };

    // ========== Action Confirm Handlers ==========

    const onCopyConfirm = async (result: CopyToListResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'copy_to_list',
        ...selection,
        source_list_id: props.id,
        target_list_id: result.targetListId,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.copySuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onMoveConfirm = async (result: MoveToListResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'move_to_list',
        ...selection,
        source_list_id: props.id,
        target_list_id: result.targetListId,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.moveSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onCreateConfirm = async (result: CreateListResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'create_new_list',
        ...selection,
        source_list_id: props.id,
        new_list_name: result.listName,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.createListSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onSubscribeConfirm = async (result: SubscribeResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);

      // Build channels object with selected channels
      const channels: Record<string, boolean> = {};
      if (result.channels.email) channels.email = true;
      if (result.channels.sms) channels.sms = true;
      if (result.channels.whatsapp) channels.whatsapp = true;

      const payload = {
        action_type: 'subscribe',
        ...selection,
        source_list_id: props.id,
        channels,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.subscribeSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onUnsubscribeConfirm = async (result: UnsubscribeResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);

      // Build channels object with selected channels
      const channels: Record<string, boolean> = {};
      if (result.channels.email) channels.email = true;
      if (result.channels.sms) channels.sms = true;
      if (result.channels.whatsapp) channels.whatsapp = true;

      const payload = {
        action_type: 'unsubscribe',
        ...selection,
        source_list_id: props.id,
        channels,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.unsubscribeSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onDeleteFromListConfirm = async (result: DeleteFromListResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'delete_from_list',
        ...selection,
        source_list_id: props.id,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.deleteFromListSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onDeleteFromAllListsConfirm = async (result: DeleteFromAllListsResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'delete_from_all_lists',
        ...selection,
        source_list_id: props.id,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.deleteFromAllListsSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    /**
     * RGPD (SC-3851) : efface définitivement les données personnelles des contacts
     * sélectionnés. Le mot de confirmation tapé par l'utilisateur est transmis à
     * l'API, qui le revérifie. Irréversible.
     */
    const onGdprEraseConfirm = async (result: GdprEraseResult) => {
      if (!result.rowId) return;
      setLoading(true);
      // Toujours un seul contact : la sélection n'est jamais reprise ici, et le
      // serveur refuse de son côté toute requête portant sur plus d'un contact.
      const payload = {
        action_type: 'gdpr_erase',
        selection_type: 'manual',
        selected_ids: [String(result.rowId)],
        source_list_id: props.id,
        confirmation: result.confirmation,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.gdprEraseSuccess',
        false,
      );
      setLoading(false);
    };

    /**
     * Libellé et classe CSS du statut d'abonnement d'une ligne sur un canal.
     * La décision est centralisée dans le composable, seule la présentation
     * reste locale.
     */
    const channelState = (
      channel: SubscriptionChannel,
      row: Record<string, any>,
    ): { label: string; cssClass: string } => {
      const presentation: Record<ChannelSubscriptionStatus, { label: string; cssClass: string }> = {
        no_coordinate: { label: t('myLists.manage.customers.expansion.status.notSubscribed'), cssClass: 'status-not-subscribed' },
        unsubscribed: { label: t('myLists.manage.customers.expansion.status.unsubscribed'), cssClass: 'status-unsubscribed' },
        subscribed: { label: t('myLists.manage.customers.expansion.status.subscribed'), cssClass: 'status-subscribed' },
        double_optin: { label: t('myLists.manage.customers.expansion.status.doubleOptin'), cssClass: 'status-double-optin' },
        simple_optin: { label: t('myLists.manage.customers.expansion.status.simpleOptin'), cssClass: 'status-simple-optin' },
        not_subscribed: { label: t('myLists.manage.customers.expansion.status.notSubscribed'), cssClass: 'status-not-subscribed' },
      };

      const coordinates: Record<SubscriptionChannel, unknown> = {
        email: row.email,
        sms: row.phone,
        push: row.push,
      };
      const optins: Record<SubscriptionChannel, number | string | null | undefined> = {
        email: row.optin_email,
        sms: row.optin_sms,
        push: null,
      };
      const unsubs: Record<SubscriptionChannel, number | string | null | undefined> = {
        email: row.unsub_email,
        sms: row.unsub_sms,
        push: row.unsub_push,
      };

      return presentation[getChannelSubscriptionStatus(
        channel,
        !!coordinates[channel],
        optins[channel],
        unsubs[channel],
      )];
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        setLoading(false);
      }
    };

    return {
      columns,
      t,
      countRequestListImport,
      listName,
      isListSegmented,
      childComponentRef,
      apiCall,
      formatPhoneNumber,
      formatFullName,
      cleanStr,
      channelState,
      // Actions
      groupedActions,
      rowActions,
      copyToListRef,
      moveToListRef,
      createListRef,
      subscribeRef,
      unsubscribeRef,
      deleteFromListRef,
      deleteFromAllListsRef,
      gdprEraseRef,
      onCopyConfirm,
      onMoveConfirm,
      onCreateConfirm,
      onSubscribeConfirm,
      onUnsubscribeConfirm,
      onDeleteFromListConfirm,
      onDeleteFromAllListsConfirm,
      onGdprEraseConfirm,
      showImportModal,
      showSegmentationEditor,
      handleManageSegmentation,
      handleImportReports,
      onSegmentationSaved,
      handleExport,
    };
  },

});
